import { useEffect, useMemo } from 'react';
import { getApiAxios } from '@thesis3/api-client';
import { useAuth } from './auth/AuthProvider';
import {
  configFailed,
  configLoaded,
  configLoading,
  store,
  useAppDispatch,
  useAppSelector,
} from './store';
import de from './locales/de.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import hi from './locales/hi.json';
import ko from './locales/ko.json';
import zh from './locales/zh.json';

/**
 * Shell-side copy of the workbench translator (apps/workbench/src/i18n.ts).
 *
 * The shell cannot depend on the workbench package, so the ~30 lines of lookup
 * logic are duplicated here. The catalogues in `./locales` are GENERATED subsets of the
 * workbench's (only the keys the shell uses; `i18n-check --root ../shell/src --emit-locales
 * ../shell/src/locales`), keyed by the English source string. A miss (or an
 * empty value) returns the English source. Chrome only — user-entered data
 * never goes through t().
 */
export type Lang = 'en' | 'es' | 'fr' | 'de' | 'zh' | 'hi' | 'ko';

const LOCALES: Record<Exclude<Lang, 'en'>, Record<string, string>> = { es, fr, de, zh, hi, ko };

const KNOWN_LANGS: ReadonlySet<string> = new Set(['en', 'es', 'fr', 'de', 'zh', 'hi', 'ko']);

export function resolveLang(language: string | undefined | null): Lang {
  const lang = (language ?? 'en').toLowerCase();
  return (KNOWN_LANGS.has(lang) ? lang : 'en') as Lang;
}

/** `Close|dialog`: everything after the first `|` is a translator note, never shown. */
function stripContext(text: string): string {
  const bar = text.indexOf('|');
  return bar > 0 ? text.slice(0, bar) : text;
}

export function translate(lang: Lang, text: string): string {
  if (lang === 'en') return stripContext(text);
  const table = LOCALES[lang];
  const hit = Object.prototype.hasOwnProperty.call(table, text) ? table[text] : undefined;
  return typeof hit === 'string' && hit !== '' ? hit : stripContext(text);
}

export function translator(lang: Lang): (text: string) => string {
  return (text) => translate(lang, text);
}

/** Replace `{name}` tokens only (single pass); unknown tokens stay as written. */
export function tf(
  t: (text: string) => string,
  text: string,
  params: Record<string, string | number>
): string {
  return t(text).replace(/\{(\w+)\}/g, (token, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : token
  );
}

/**
 * The tenant language (`locale.language` from /api/config), fetched once per
 * session once somebody is signed in and cached in the `config` store slice.
 * English until it lands, before sign-in, and if the read fails.
 */
export function useLang(): Lang {
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const language = useAppSelector((s) => s.config.language);
  const signedIn = user !== null;

  useEffect(() => {
    if (!signedIn) return;
    // Read the LIVE store state, not a render-time closure: every useT() caller
    // runs this effect, and only the first may fire the request.
    if (store.getState().config.status !== 'idle') return;
    dispatch(configLoading());
    getApiAxios()
      .get<{ locale?: { language?: string } }>('/api/config')
      .then((res) => dispatch(configLoaded(res.data?.locale?.language ?? null)))
      .catch(() => dispatch(configFailed()));
  }, [signedIn, dispatch]);

  return resolveLang(language);
}

export function useT(): (text: string) => string {
  const lang = useLang();
  return useMemo(() => translator(lang), [lang]);
}
